'use client';

import './styles/@theme';
import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useStore } from '@/store/store';

import type { SiteWideJackpotData } from '@/types/aggregation/site-wide-jackpot';
import type { Region } from '@/types/constants';
import type { Game as GameType } from '@/types/games';
import type { SiteWideJackpotSlicePrimary } from '@/types/zones/slices/site-wide-jackpot-slice';

import { DraggableContainer } from '@/app/elements/draggable-wrapper/draggable-container';

import { classes } from '@/utils/classes/classes';
import { getText } from '@/utils/text/text';
import { AMPLITUDE_EXPERIMENTS, getExperimentValue } from '@/utils/experiments';
import { getPathFromLink } from '@/logic/page-config/navigation/navigation-adapter';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { GameCard } from '../subcomponents/game-card/game-card';
import { ConfirmationZone } from '@/app/components/site-wide-jackpot/subcomponents/confirmation-zone/confirmation-zone';
import { SwjRulesInfoButton } from '@/app/components/site-wide-jackpot/subcomponents/swj-rules-info-button/swj-rules-info-button';

import { parseGamesList } from '@/logic/games/parse-games-list';
import { GameFactoryClient } from '@/app/components/game/game-factory.client';
import { PRISMIC_ZONE_LAYOUT } from '@/app/constants/zone';
import { splitIntoGroups } from '@/utils/array/array-helper';
import { mapSWJInfo } from '@/app/components/site-wide-jackpot/hooks/use-prismic-jackpot-info';
import { useSwjRulesNavigation } from '@/app/components/site-wide-jackpot/hooks/use-swj-rules-navigation';
import { JackpotCard } from '@/app/components/site-wide-jackpot/subcomponents/jackpot-card';
import { JACKPOTS_ZONE_NAME, LOCAL_STORAGE, ROUTES } from '@/utils/constants';
import { store, getSessionId } from '@/utils/storage';
import { useShowDYBInterstitial } from '@/app/components/site-wide-jackpot/hooks/use-show-dyb-interstitial/use-show-dyb-interstitial';
import { incrementDYBTutorialCounter } from '@/logic/dyb-interstatial/dyb-interstatial-helper';
import { ScrollingBanner } from '../subcomponents/scrolling-banner/scrolling-banner';
import type { SupportLink } from '@/types/layout/link-view-model';
import { useGlobalContext } from '@/store/contexts/global-context';
import { POPUPS } from '@/app/components/popup/constants';
import { getSeenZones } from '@/utils/exclusive-popup';
import { getSwjCtaClickedProps } from '@/tracking/amplitude/events/general/swj-cta-info';
import type { Tutorial } from '@/app/components/site-wide-jackpot/how-it-works/types';
import { SLICE_TYPE } from '@/utils/constants';
import type {
    SiteWideJackpotMultiplierBoostItem,
    SiteWideJackpotMultiplierBoostPrimary
} from '@/types/aggregation/site-wide-jackpot';
import type { PrismicSlice } from '@/types/prismic/common';
import { useLobbyMask } from '@/app/components/site-wide-jackpot/hooks/use-mask';
import { trackJackpotsEvent } from '@/tracking/amplitude/events/general/jackpots';
import { AnimationAsset } from '../../animation-asset/animation-asset';

type LobbyZoneProps = {
    swjData: SiteWideJackpotData;
    content: SiteWideJackpotSlicePrimary;
    isMobileOrTablet?: boolean;
    support?: SupportLink;
    exclusiveZoneEnabled?: boolean;
    forceHideGameInfo?: boolean;
};

const GAMES_PER_CARD = 3;

// adr TODO look into this

export const SiteWideJackpotZone = ({
    swjData,
    content,
    isMobileOrTablet,
    support,
    exclusiveZoneEnabled = false,
    forceHideGameInfo
}: LobbyZoneProps) => {
    const router = useRouter();
    const isLoggedIn = useStore((state) => state.userContext.isLoggedIn);
    const region = useStore((state) => state?.regionInfo?.currentRegion?.region) as Region;

    const multiplier = useStore((state) => state.siteWideJackpot.multiplier);
    const jackpotData = mapSWJInfo(swjData);
    const zoneContent = content;
    const { handleShowLoading } = useStore((state) => state.loading);
    const { isAndroidWrapper, experiments } = useGlobalContext();
    const isSWJTutorialV2 = getExperimentValue(experiments, AMPLITUDE_EXPERIMENTS.SWJ_INTERSTITIAL_V2) === 'treatment';
    const { sendEvent } = useStore((state) => state.tracking);

    const openInBrowser = !!jackpotData.openRulesExternally && isAndroidWrapper;
    const multiplierBoostSlice = zoneContent?.jackpot?.data?.body?.find(
        (
            slice
        ): slice is PrismicSlice<
            'jackpot_multiplier_boost',
            SiteWideJackpotMultiplierBoostItem[],
            SiteWideJackpotMultiplierBoostPrimary
        > => slice.slice_type === SLICE_TYPE.SITE_WIDE_JACKPOT_MULTIPLIERS
    );
    const tutorial = multiplierBoostSlice?.primary?.tutorial as Tutorial | null | undefined;
    const hasTutorial = !!tutorial;
    const siteWideJackpotOn = jackpotData.enabled;
    const exclusiveSeen = typeof window !== 'undefined' ? getSeenZones() : [];
    const hasAtLeastOneExclusiveSeen = Array.isArray(exclusiveSeen) && exclusiveSeen.length > 0;
    const { showDYBInterstitial, setShowDYBInterstitial } = useShowDYBInterstitial(
        isLoggedIn,
        siteWideJackpotOn,
        exclusiveZoneEnabled,
        hasAtLeastOneExclusiveSeen,
        isSWJTutorialV2
    );
    const showDYBTutorial = useStore((state) => state.siteWideJackpot.showDYBTutorial);
    const showDYBTutorialCounter = useStore((state) => state.siteWideJackpot.showDYBTutorialCounter);
    const showDYBTutorialV2 = useStore((state) => state.siteWideJackpot.showDYBTutorialV2);
    const showDYBTutorialV2Counter = useStore((state) => state.siteWideJackpot.showDYBTutorialV2Counter);
    const activeTutorialFlag = isSWJTutorialV2 ? showDYBTutorialV2 : showDYBTutorial;
    const activeTutorialCounter = isSWJTutorialV2 ? showDYBTutorialV2Counter : showDYBTutorialCounter;
    const setDYBUserPreferences = useStore((state) => state.siteWideJackpot.setDYBUserPreferences);
    const queuePopup = useStore((state) => state.popup.queuePopup);
    const linkedCategory =
        getPathFromLink({
            type: 'category',
            uid: zoneContent?.category?.uid || '',
            lang: 'en',
            id: '',
            link_type: 'Document',
            key: ''
        }) || undefined;

    const jackpotPromotedGames = mapSWJInfo(zoneContent?.jackpot?.data);
    const groupedGames = isMobileOrTablet
        ? splitIntoGroups(jackpotPromotedGames.games, GAMES_PER_CARD)
        : jackpotPromotedGames.games;

    const { handleRulesClick, handleInfoButtonClick } = useSwjRulesNavigation({
        openInBrowser,
        rulesPath: jackpotData.rulesPath,
        navigationSource: 'home'
    });

    const viewAllGames = () => {
        if (linkedCategory) {
            sendEvent([
                {
                    destination: 'amplitude',
                    eventProps: getSwjCtaClickedProps({
                        linkUrl: linkedCategory
                    })
                }
            ]);
            handleShowLoading();
            router.push(linkedCategory);
        }
    };

    const wrapperRef = useRef<HTMLDivElement>(null);
    const draggableWrapperRef = useRef<HTMLDivElement | null>(null);
    const { maskStyle, isMaskVisible, setMaskImage } = useLobbyMask(draggableWrapperRef, wrapperRef);

    // Get reference to the draggable-wrapper element
    useEffect(() => {
        if (wrapperRef.current) {
            const draggableContainer = wrapperRef.current.querySelector('.draggable-container') as HTMLDivElement;
            const draggableWrapper = wrapperRef.current.querySelector('.draggable-wrapper') as HTMLDivElement;
            if (draggableContainer && draggableWrapper) {
                draggableWrapperRef.current = draggableWrapper;
                setMaskImage();
            }
        }
    }, [setMaskImage]);

    useEffect(() => {
        const draggableWrapper = draggableWrapperRef.current;
        if (draggableWrapper) {
            const handleScroll = () => setMaskImage();
            draggableWrapper.addEventListener('scroll', handleScroll, { passive: true });
            return () => {
                draggableWrapper.removeEventListener('scroll', handleScroll);
            };
        }
    }, [setMaskImage]);

    useEffect(() => {
        if (activeTutorialFlag && hasTutorial && showDYBInterstitial) {
            queuePopup({
                type: POPUPS.DYB_INTERSTITIAL,
                params: {
                    tutorial: tutorial,
                    logoUrl: jackpotData.alternateLogo,
                    swjCategory: zoneContent?.category,
                    isSWJTutorialV2: isSWJTutorialV2
                }
            });
            // Increment view counter and mark tutorial as shown
            incrementDYBTutorialCounter(isSWJTutorialV2, activeTutorialCounter ?? 0);
            if (isSWJTutorialV2) {
                setDYBUserPreferences({ showDYBTutorialV2: false });
            } else {
                setDYBUserPreferences({ showDYBTutorial: false });
            }
            // Store the session ID when interstitial is shown
            const sessionId = getSessionId();
            if (sessionId) {
                store({ key: LOCAL_STORAGE.DYB_INTERSTITIAL_SHOWN as any, value: sessionId }, 'local');
            }
            const trackingData = trackJackpotsEvent('jackpotsIntroViewed');
            sendEvent([trackingData.event]);
            setShowDYBInterstitial(false);
        }
    }, [
        activeTutorialFlag,
        showDYBInterstitial,
        hasTutorial,
        activeTutorialCounter,
        isSWJTutorialV2,
        setDYBUserPreferences,
        queuePopup,
        setShowDYBInterstitial,
        tutorial,
        jackpotData.alternateLogo,
        zoneContent?.category
    ]);

    return (
        <div
            className={classes('site-wide-jackpot', !isMobileOrTablet && 'desktop')}
            data-test-id="wrapper-site-wide-jackpots"
            data-t-isswj="true"
            data-t-zonename={JACKPOTS_ZONE_NAME}
            data-t-zonelayout="jackpots">
            <div
                className={classes(
                    'site-wide-jackpot-module',
                    isLoggedIn && 'logged-in',
                    !isMobileOrTablet && 'desktop'
                )}>
                {isMobileOrTablet && (
                    <SwjRulesInfoButton
                        extraClassName="site-wide-jackpot-info-button-mobile"
                        href={openInBrowser ? jackpotData.rulesPath || '' : ROUTES.SITE_WIDE_JACKPOT_RULES}
                        openInBrowser={openInBrowser}
                        onClickEvent={handleInfoButtonClick}
                    />
                )}
                <div className="logo-container">
                    {!isMobileOrTablet && (
                        <SwjRulesInfoButton
                            extraClassName="site-wide-jackpot-info-button"
                            href={openInBrowser ? jackpotData.rulesPath || '' : ROUTES.SITE_WIDE_JACKPOT_RULES}
                            openInBrowser={openInBrowser}
                            onClickEvent={handleInfoButtonClick}
                        />
                    )}
                    <AnimationAsset
                        url={jackpotData.animatedLogo || jackpotData.logo || ''}
                        className="logo"
                        alt="Logo"
                    />
                </div>
                <div className="content-details">
                    <div className="content-intro">{jackpotData.introTitle}</div>
                    <div className="content-headline">{jackpotData.subHeadline}</div>
                </div>

                <div className="wrapper gapped view-all-games">
                    <div className="play-jackpot-games">
                        {getText({ text: 'components.siteWideJackpots.playJackpotGames' })}
                    </div>
                    <div
                        className="view-all"
                        data-t-module="fdc_jackpots"
                        onClick={() => viewAllGames()}
                        onKeyDown={() => viewAllGames()}>
                        {getText({ text: 'components.siteWideJackpots.viewAll' })}
                        <span className="arrow-right"></span>
                    </div>
                </div>

                <div className="wrapper gapped" ref={wrapperRef}>
                    {isMaskVisible && !isMobileOrTablet && <div className="fade-wrapper" style={maskStyle}></div>}
                    <DraggableContainer indicators={true}>
                        {[
                            <div key="jackpot-card" className="jackpot-slide">
                                <JackpotCard
                                    jackpotId={jackpotData.providerJackpotId || ''}
                                    enableJustWonAnimation={true}
                                    enableWonXTimesAnimation={true}
                                />
                            </div>,
                            ...(!!groupedGames.length
                                ? isMobileOrTablet
                                    ? (groupedGames as GameType[][]).map((gamesGroup: GameType[]) => (
                                          <div className="game-slide" key={gamesGroup.map((game) => game.uid).join('')}>
                                              <GameCard
                                                  games={gamesGroup}
                                                  gtmConfig={{ module: 'site_wide_jackpot' }}
                                                  region={region}
                                                  isMobile={Boolean(isMobileOrTablet)}
                                                  forceTextBadge={true}
                                                  forceHideGameInfo={forceHideGameInfo}
                                              />
                                          </div>
                                      ))
                                    : (groupedGames as GameType[]).map((rawGame: GameType, index: number) => {
                                          const { games: adapted } = parseGamesList(
                                              [rawGame],
                                              PRISMIC_ZONE_LAYOUT.DEFAULT,
                                              {
                                                  region,
                                                  isMobile: Boolean(isMobileOrTablet)
                                              }
                                          );
                                          const game = adapted[0];
                                          return (
                                              <div className="game-slide" key={rawGame.uid}>
                                                  <GameFactoryClient
                                                      game={game}
                                                      layout={PRISMIC_ZONE_LAYOUT.DEFAULT}
                                                      forceTextBadge={true}
                                                      tracking={{ gamePosition: index }}
                                                      hideGameInfo={isLoggedIn}
                                                  />
                                              </div>
                                          );
                                      })
                                : [])
                        ]}
                    </DraggableContainer>
                </div>
                <div className="optin-details" data-test-id="button-continue-opt-in">
                    <ScrollingBanner jackpotId={jackpotData.providerJackpotId || ''} />
                    <div className={classes('optin-details-dark', multiplier ? 'opted-in' : '')}>
                        <ConfirmationZone
                            currentMultiplier={multiplier || 0}
                            multipliers={jackpotData.multipliers || []}
                            description={jackpotData.oneLiner || ''}
                            shortRules={jackpotData.dybShortRules || ''}
                            logoUrl={jackpotData.alternateLogo || ''}
                            handleRulesClick={handleRulesClick}
                            jackpotId={jackpotData.providerJackpotId || ''}
                            support={support}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
